'use client';

import { Alert, AlertDescription } from '@govinity/ui/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@govinity/ui/components/ui/dialog';
import { Button } from '@govinity/ui/components/ui/button';
import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';
import { LoadingError } from '@govinity/ui/components/ui-custom/loading-error';
import { useToast } from '@govinity/ui/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import api from '@/lib/api';
import { CommentForm } from './comment-form';
import {
  flattenCommentEntries,
  hasSelectableEntry,
  publicCommentOptionsQueryKey,
  publicCommentUserCommentsQueryKey,
} from './helpers';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

type CommentDialogProps = {
  onOpenChange: (open: boolean) => void;
};

export const CommentDialog = ({ onOpenChange }: CommentDialogProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const t = useTranslations('PortalPublicComments');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    data: options,
    error: optionsError,
    isLoading,
  } = useQuery({
    queryKey: publicCommentOptionsQueryKey(government.code, meeting.id),
    queryFn: () => api.governments.publicComments(government.code, meeting.id).getOptions(),
    refetchOnMount: 'always',
  });

  const handleCommentSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: publicCommentUserCommentsQueryKey(government.code, meeting.id),
    });
    toast({ title: t('submittedToast') });
    setIsSubmitted(true);
  };

  const dialogBody = () => {
    if (isSubmitted) {
      return (
        <div className="space-y-4">
          <p className="flex items-start gap-2 text-sm">
            <CircleCheck
              className="text-success-foreground mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            {t('submittedDescription', { government: government.name })}
          </p>
          <Button className="w-full" onClick={() => onOpenChange(false)} variant="outline">
            {t('closeButton')}
          </Button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-center py-4">
          <LoadingSpinner size={32} aria-label={t('loadingOptionsAriaLabel')} />
        </div>
      );
    }

    if (optionsError) {
      return <LoadingError message={t('optionsLoadError')} />;
    }

    const entries = flattenCommentEntries(options?.sections ?? []);

    if (!hasSelectableEntry(entries)) {
      return <p className="text-muted-foreground text-sm">{t('noEntries')}</p>;
    }

    return <CommentForm entries={entries} onSuccess={handleCommentSuccess} />;
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="grid-cols-1 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isSubmitted ? t('submittedTitle') : t('dialogTitle')}</DialogTitle>
        </DialogHeader>
        {!isSubmitted && (
          <Alert className="my-2" variant="warning">
            <CircleAlert className="h-4 w-4" />
            <AlertDescription>
              {t('dialogDescription', { governmentName: government.name })}
            </AlertDescription>
          </Alert>
        )}

        {dialogBody()}
      </DialogContent>
    </Dialog>
  );
};
